import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Ticket } from 'lucide-react';
import type { DayPassOfferDto } from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { useResource } from '@/features/settings/useSettings';
import { ApiError } from '@/lib/api';
import {
  DAY_PASSES_PATH,
  deleteDayPassOffer,
  sellableOffers,
  type DayPassScreen,
} from './dayPasses';
import { OfferDialog } from './OfferDialog';
import { OfferList } from './OfferList';
import { PassList } from './PassList';
import { SellPassDrawer } from './SellPassDrawer';

/**
 * Boot Camp passes (SPEC §6.1) — the screen that makes `enableBootCampOffer`
 * mean something.
 *
 * The toggle had existed since the first migration with nothing reading it, so
 * switching it on changed nothing anywhere. It now decides whether this screen
 * can sell, and when it is off the screen says so and points at the setting
 * instead of offering a button the server would refuse.
 *
 * Two halves because they are two jobs at different times of day: an owner sets
 * up what the venue sells, and a cashier sells it. Managing offers needs
 * `settings.manage`; selling needs `sessions.manage`, the same level as opening
 * a session — a pass is time the customer is paying for up front, not a
 * discount — and `bills.create`, because the sale is paid there and then.
 */
export function DayPassesPage() {
  const { t } = useTranslation();
  const { can, venue } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';
  const canManageOffers = can('settings.manage');
  /*
   * A sale takes the money on the spot — it writes a PAID bill and files the
   * cash into the seller's box — so it wants `bills.create` beside
   * `sessions.manage` (2026-09-24), like the till's own checkout. Without it
   * the button stays, shut, with the reason on it.
   */
  const canTakePayment = can('bills.create');
  const canOpenSessions = can('sessions.manage');
  const canSell = canOpenSessions && canTakePayment;
  /*
   * The reason names exactly the keys that are MISSING. Naming Create bills
   * whenever it was absent told someone holding neither to get Create bills —
   * and once the owner ticked it the button stayed shut with no reason at all.
   */
  const sellRefusal = canSell
    ? undefined
    : !canOpenSessions && !canTakePayment
      ? t('dayPasses.sell.needsSessionsAndBills')
      : !canOpenSessions
        ? t('dayPasses.sell.needsSessionsManage')
        : t('dayPasses.sell.needsBillsCreate');

  const { data, loading, error, reload } = useResource<DayPassScreen>(DAY_PASSES_PATH);

  const [selling, setSelling] = useState(false);
  const [editingOffer, setEditingOffer] = useState<DayPassOfferDto | null>(null);
  const [addingOffer, setAddingOffer] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // "Live" / "Ended" on a sold pass turns over on the clock, not on a fetch, so
  // the minute hand is its own state — same idea as the Device List's tick.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  async function handleDelete(offer: DayPassOfferDto): Promise<void> {
    setActionError(null);
    try {
      await deleteDayPassOffer(offer.id);
      await reload();
    } catch (caught) {
      // The server refuses an offer that has passes sold on it, and its message
      // says what to do instead (switch it off). Surfaced verbatim.
      setActionError(
        caught instanceof ApiError ? caught.message : t('dayPasses.offers.loadFailed'),
      );
    }
  }

  return (
    <div className="flex h-full flex-col overflow-auto p-4">
      <header className="mb-4 flex items-start gap-3">
        <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-secondary text-primary">
          <Ticket className="h-5 w-5" aria-hidden />
        </span>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-content">{t('dayPasses.title')}</h1>
          <p className="mt-1 text-sm text-content-muted">{t('dayPasses.subtitle')}</p>
        </div>

        <button
          type="button"
          onClick={() => setSelling(true)}
          disabled={!canSell || !data?.enabled || sellableOffers(data?.offers ?? []).length === 0}
          title={sellRefusal ?? (data?.enabled ? undefined : t('dayPasses.sell.disabledReason'))}
          data-sell-pass
          className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('dayPasses.sell.open')}
        </button>
      </header>

      {/* Printed as well as on the button's title: a title needs a hover, and
          a phone has none, so a greyed Sell a pass would give no reason. The
          Boot Camp-off case already has its own panel below. */}
      {sellRefusal ? (
        <p data-sell-refusal className="mb-4 text-sm text-content-muted">
          {sellRefusal}
        </p>
      ) : null}

      {data && !data.enabled ? (
        <section
          data-boot-camp-off
          className="mb-4 rounded-card border border-subtle bg-raised p-4"
        >
          <h2 className="text-sm font-semibold text-content">{t('dayPasses.disabled.title')}</h2>
          <p className="mt-1 text-sm text-content-secondary">{t('dayPasses.disabled.body')}</p>
          {canManageOffers ? (
            <Link
              to="/settings/general"
              className="mt-2 inline-block text-sm text-primary hover:underline"
            >
              {t('dayPasses.disabled.link')}
            </Link>
          ) : null}
        </section>
      ) : null}

      {error ? (
        <p role="alert" className="mb-4 text-sm text-danger">
          {error}
        </p>
      ) : null}
      {actionError ? (
        <p role="alert" className="mb-4 text-sm text-danger">
          {actionError}
        </p>
      ) : null}

      {loading && !data ? (
        <p className="text-sm text-content-muted">{t('common.loading')}</p>
      ) : (
        <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
          <OfferList
            offers={data?.offers ?? []}
            canManage={canManageOffers}
            currencySymbol={currencySymbol}
            onAdd={() => setAddingOffer(true)}
            onEdit={setEditingOffer}
            onDelete={(offer) => void handleDelete(offer)}
          />

          <PassList passes={data?.passes ?? []} nowMs={nowMs} currencySymbol={currencySymbol} />
        </div>
      )}

      {selling && data && canSell ? (
        <SellPassDrawer
          offers={sellableOffers(data.offers)}
          enabled={data.enabled}
          closesAt={data.closesAt}
          nowMs={nowMs}
          currencySymbol={currencySymbol}
          onClose={() => setSelling(false)}
          onSold={() => {
            setSelling(false);
            void reload();
          }}
        />
      ) : null}

      {addingOffer || editingOffer ? (
        <OfferDialog
          offer={editingOffer}
          onClose={() => {
            setAddingOffer(false);
            setEditingOffer(null);
          }}
          onSaved={() => {
            setAddingOffer(false);
            setEditingOffer(null);
            void reload();
          }}
        />
      ) : null}
    </div>
  );
}
